import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { BASE_COLORS } from 'lib/functions/const/colors';
import { formatDate, formatMoney } from 'lib/functions/utils';
import React from 'react';

import { RawSavedEntryViewProps } from './types';

const RawSavedEntryView: React.FC<RawSavedEntryViewProps> = ({
  savedEntry,
}) => {
  const totalDebit = savedEntry.lines.reduce((sum, l) => sum + l.debit, 0);
  const totalCredit = savedEntry.lines.reduce((sum, l) => sum + l.credit, 0);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1.5,
          pb: 1,
          borderBottom: '2px solid #0288d1',
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 600, color: '#0288d1' }}
        >
          Asiento #{savedEntry.id}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: BASE_COLORS.gray, fontWeight: 500 }}
        >
          {formatDate(new Date(savedEntry.date), 'dd-mm-yyyy')}
        </Typography>
      </Box>

      <TableContainer sx={{ flex: 1, maxHeight: 300, overflow: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  bgcolor: `${BASE_COLORS.softGray}40`,
                }}
              >
                Cuenta
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>
                Nombre
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>
                Descripcion
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>
                Doc
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontWeight: 'bold',
                  bgcolor: `${BASE_COLORS.softGray}40`,
                }}
              >
                Debe
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontWeight: 'bold',
                  bgcolor: `${BASE_COLORS.softGray}40`,
                }}
              >
                Haber
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {savedEntry.lines.map((line, idx) => (
              <TableRow key={idx}>
                <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
                  {line.node?.accountCode}
                </TableCell>
                <TableCell
                  sx={{
                    maxWidth: 100,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: '0.75rem',
                  }}
                  title={line.node?.key}
                >
                  {line.node?.key}
                </TableCell>
                <TableCell
                  sx={{
                    maxWidth: 120,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: '0.75rem',
                  }}
                  title={line.description}
                >
                  {line.description}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
                  {line.documentKey}
                </TableCell>
                <TableCell align="right" sx={{ fontSize: '0.8rem' }}>
                  {formatMoney(line.debit)}
                </TableCell>
                <TableCell align="right" sx={{ fontSize: '0.8rem' }}>
                  {formatMoney(line.credit)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 3,
          mt: 1,
          pt: 1,
          borderTop: '2px solid #0288d1',
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
          Totales:
        </Typography>
        <Typography
          variant="body2"
          sx={{ fontWeight: 'bold', minWidth: 80, textAlign: 'right' }}
        >
          {formatMoney(totalDebit)}
        </Typography>
        <Typography
          variant="body2"
          sx={{ fontWeight: 'bold', minWidth: 80, textAlign: 'right' }}
        >
          {formatMoney(totalCredit)}
        </Typography>
      </Box>
    </Box>
  );
};

export default RawSavedEntryView;
